import React, {
  useState,
  createContext,
  ChangeEvent,
  ReactNode,
  FC,
  useMemo,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from '@Iot-Bee/standard-web-library';
import { Calibration, DeviceData, GraphData } from '../../../Types/types';
import dayjs, { Dayjs } from 'dayjs';
import { ChartContextType, ChartOptions } from './interfaces';
import {
  filterDataByDateRange,
  filterESTLabels,
  findOldestDataDate,
  getOldData,
  initGraph,
  initIsPowerDevice,
} from '../util/contextutils';
import { storeCalibration } from '../util/util';
import { noCacheFetcher } from '../../../utils/fetch';
import { DevicesContext } from '../../../context/DevicesContext';

export const calculatedKwhSensor = 'kwh_delta_sum';

export const ChartContext = createContext<ChartContextType | undefined>(undefined);

interface ChartContextProviderProps {
  children: ReactNode;
}

export const ChartContextProvider: FC<ChartContextProviderProps> = ({ children }) => {
  const deviceContext = useContext(DevicesContext);

  if (!deviceContext) {
    throw new Error('No device context!');
  }

  const { devices } = deviceContext;

  const {
    actions: { openSnackbar },
  } = useSnackbar();
  const { device } = useParams(); // id of device chosen
  const isPowerDevice = useMemo(() => initIsPowerDevice(device || '', devices), [device]);

  const [deviceData, setDeviceData] = useState<DeviceData[]>([]); // data from the device
  const [deviceDataLoading, setDeviceDataLoading] = useState<boolean>(true); // loading state

  const [availableSensors, setAvailableSensors] = useState<string[]>([]); //available sensors
  const [calibrations, setCalibrations] = useState<Calibration[]>([]); //available calibrations

  const [chartOptions, setChartOptions] = useState<ChartOptions>({});

  //This is for when the user goes back in time and fetches more data, we dont want that in the localstorage
  const historicData = useRef<DeviceData[]>([]);
  const [loadingHistoricData, setLoadingHistoricData] = useState<boolean>(false);

  // This is the data that is displayed in the graph
  const graphData: GraphData[] = useMemo(() => {
    if (!chartOptions.sensor) return [];

    const data = [...historicData.current, ...deviceData];

    const graphData = filterDataByDateRange(data, chartOptions);

    if (Object.keys(chartOptions).length > 0) {
      sessionStorage.setItem(`chartOptions-${device}`, JSON.stringify(chartOptions));
    }

    return graphData;
  }, [deviceData, chartOptions, historicData]);

  useEffect(() => {
    if (!device || !devices.length) return;

    // Check if we have any chart options in the session storage
    const sessionOptions = JSON.parse(sessionStorage.getItem(`chartOptions-${device}`) ?? '{}', (key, value) => {
      if (key === 'fromDate' || key === 'toDate') {
        return dayjs(value);
      }
      return value;
    }) as ChartOptions;

    initGraph(device as string, sessionOptions.fromDate)
      .then((response) => {
        const calibrations = response.calibrations;
        const sensors = response.sensors;
        const deviceData = response.deviceData;

        const labels = sensors.map((sensor) => sensor.sensor);

        // isPowerDevice might not have been calculated yet
        const uniqueLabels = initIsPowerDevice(device, devices) ? filterESTLabels(labels) : labels;
        
        // Find the sensor to show initially
        const startSensorString = sessionOptions.sensor
          ? sessionOptions.sensor
          : (uniqueLabels.find((label) => label.includes('kwh')) ?? uniqueLabels[0]);
        const startSensor = sensors.find((sensor) => sensor.sensor === startSensorString);

        // If the initial sensor has a calibration, find it
        let startCalibration: Calibration | undefined = undefined;
        if (startSensor && startSensor.calibrationId) {
          startCalibration = calibrations.find((calibration) => calibration.id === startSensor.calibrationId);
        }

        let fromDate: Dayjs | undefined = undefined;
        let toDate: Dayjs | undefined = undefined;

        if (deviceData.length === 0) {
          openSnackbar('info', 'No data available');
        } else {
          // Set the dates for the graph data
          fromDate = dayjs(new Date(deviceData[0].time));
          toDate = dayjs(new Date(deviceData[deviceData.length - 1].time));
        }

        setChartOptions(() => {
          return { ...sessionOptions, sensor: startSensorString, calibration: startCalibration, fromDate, toDate };
        });

        setDeviceData(response.deviceData);
        setAvailableSensors(uniqueLabels);
        setCalibrations(response.calibrations);
      })
      .catch((e) => {
        console.error(e);
        openSnackbar('error', 'Could not initialize graph');
      })
      .finally(() => {
        setDeviceDataLoading(false);
      });
  }, [device, devices]);

  const handleChangeSensor = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!device) return;

    const selectedSensor = e.target.value;

    if (selectedSensor === chartOptions.sensor) return;

    const newOptions = { ...chartOptions, sensor: selectedSensor };
    setChartOptions(newOptions);

    // If no from date is set, we already have all the data we need
    if (chartOptions.fromDate === undefined) {
      return;
    }

    setLoadingHistoricData(true);
    getOldData(device, newOptions, deviceData ?? [])
      .then((res) => {
        if ('error' in res) {
          openSnackbar('error', res.error);
          return;
        }
        const data = res.data;

        let fromDate: Dayjs | undefined = undefined;
        let toDate: Dayjs | undefined = undefined;

        if (data.length === 0) {
          openSnackbar('info', 'No data available');
        } else {
          // Set the dates for the graph data
          fromDate = dayjs(new Date(data[0].time));
          toDate = dayjs(new Date(data[data.length - 1].time));
        }

        setChartOptions((oldOptions) => {
          const newOptions = { ...oldOptions, fromDate, toDate };
          return newOptions;
        });

        historicData.current = data;
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoadingHistoricData(false);
      });
  };

  const handleCalibrationChange = (selectedOption: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (selectedOption === undefined) return;
    if (chartOptions.sensor === undefined) return;

    const newCalibration = calibrations.find((item) => item.name === selectedOption.target.value) || undefined;

    setChartOptions((oldOptions) => {
      const newOptions = { ...oldOptions, calibration: newCalibration };
      return newOptions;
    });
    storeCalibration(newCalibration, device as string, chartOptions.sensor)
      .then((data) => {
        openSnackbar('success', data);
      })
      .catch((err) => {
        openSnackbar('error', err);
      });
  };

  const updateData = () => {
    if (!device || !chartOptions.sensor) {
      openSnackbar('error', 'No device or sensor selected');
      return;
    }
    const sensorData = deviceData.filter((item) => item.sensor === chartOptions.sensor);
    const newestDate = dayjs(new Date(sensorData[sensorData.length - 1].time)).add(1, 'second'); // Add one second to get the next data point
    noCacheFetcher(
      process.env.REACT_APP_API_URL + `getdata/${device}/${chartOptions.sensor}?from=${newestDate.toISOString()}`
    ).then((res) => {
      if (res.status === 200) {
        res.json().then((data) => {
          if (data.length === 0) {
            openSnackbar('info', 'No new data available');
            return;
          } else {
            setDeviceData((oldData) => {
              return [...oldData, ...data];
            });
            openSnackbar('success', `Successfully fetched ${data.length} new data points`);
          }
        });
      } else {
        openSnackbar('error', 'Could not fetch new data');
      }
    });
  };

  const handleFromDateChange = (date: Dayjs) => {
    let selectedFromDate = date;
    if (selectedFromDate.isAfter(chartOptions.toDate)) {
      openSnackbar('error', 'From date cannot be after to date');
      return;
    }

    if (!chartOptions.sensor) {
      openSnackbar('error', 'No sensor selected');
      return;
    }

    const oldestDate = findOldestDataDate(deviceData, chartOptions.sensor, historicData.current);
    //Load not chached data from the server
    if (selectedFromDate.isBefore(oldestDate) && selectedFromDate.isBefore(chartOptions.fromDate)) {
      //Fetch more data from the API and place it in the historical array
      if (!device) return;
      selectedFromDate = selectedFromDate.hour(0).minute(0).second(0).millisecond(0);

      const from = selectedFromDate;
      const to = oldestDate;

      setLoadingHistoricData(true);

      getOldData(device, { ...chartOptions, toDate: to, fromDate: from }, deviceData)
        .then((res) => {
          if ('error' in res) {
            openSnackbar('error', res.error);
            return;
          }
          const data = res.data;
          const sortedData = data.sort((a, b) => {
            return Math.floor(new Date(a.time).getTime() / 1000) - Math.floor(new Date(b.time).getTime() / 1000);
          });

          const newHistoric = [...sortedData, ...historicData.current];
          historicData.current = newHistoric;
          setChartOptions((oldOptions) => {
            const newOptions = { ...oldOptions, fromDate: selectedFromDate };
            return newOptions;
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingHistoricData(false);
        });
    } else {
      const newFromDate = selectedFromDate;
      setChartOptions((oldOptions) => {
        const newOptions = { ...oldOptions, fromDate: newFromDate };
        return newOptions;
      });
    }
  };

  const handleToDateChange = (date: Dayjs) => {
    if (!device) return;
    const newToDate = date.endOf('day');

    if (newToDate.isBefore(chartOptions.fromDate)) {
      openSnackbar('error', 'To date cannot be before from date');
      return;
    }

    setChartOptions((oldOptions) => {
      const newOptions = { ...oldOptions, toDate: newToDate };
      return newOptions;
    });
  };

  const handleAccumulateToDailyChange = (accumulate: boolean) => {
    if (chartOptions.sensor !== calculatedKwhSensor) {
      openSnackbar('error', 'Only kwh can be accumulated to daily data');
      return;
    }
    setChartOptions((oldOptions) => {
      const newOptions = { ...oldOptions, accumulate: accumulate };
      return newOptions;
    });
  };

  const handleShowDataPointsChange = (showDataPoints: boolean) => {
    setChartOptions((oldOptions) => {
      const newOptions = { ...oldOptions, showDataPoints: showDataPoints };
      return newOptions;
    });
  };

  return (
    <ChartContext.Provider
      value={{
        deviceId: device || '',
        graphData,
        loadingGraphData: deviceDataLoading,
        availableSensors,
        isPowerDevice,
        calibrations,
        updateData,
        deviceData: deviceData,
        chartOptions,
        loadingHistoricData,
        setChartOptions: {
          handleCalibrationChange,
          handleFromDateChange,
          handleToDateChange,
          handleAccumulateToDailyChange,
          handleChangeSensor,
          handleShowDataPointsChange,
        },
      }}
    >
      {children}
    </ChartContext.Provider>
  );
};
